import React from 'react';
import * as FiIcons from 'react-icons/fi';

/* ── ExpandChevron ──────────────────────────────────────────────────── */

interface ExpandChevronProps {
  isExpanded: boolean;
  onClick: (e: React.MouseEvent) => void;
}

/**
 * Consistent chevron toggle button for expandable rows.
 * Rotates 180 degrees when expanded.
 */
export const ExpandChevron: React.FC<ExpandChevronProps> = ({ isExpanded, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="p-1.5 rounded-md text-monday-text-muted hover:text-monday-text hover:bg-monday-hover transition-all duration-150"
    aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
  >
    {FiIcons.FiChevronDown({
      className: `h-4 w-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`
    })}
  </button>
);

/* ── ExpandableDetailPanel ───────────────────────────────────────────── */

interface ExpandableDetailPanelProps {
  children: React.ReactNode;
  className?: string;
}

export const ExpandableDetailPanel: React.FC<ExpandableDetailPanelProps> = ({
  children,
  className = '',
}) => (
  <div className={`rounded-xl border border-monday-border-light bg-monday-header/80 p-5 shadow-xs ${className}`}>
    <div className="border-l-[3px] border-primary-500 pl-4">
      {children}
    </div>
  </div>
);

/* ── ExpandableRow (table) ──────────────────────────────────────────── */

interface ExpandableRowProps {
  /** Whether this row is currently expanded */
  isExpanded: boolean;
  /** Number of columns to span in the table */
  colSpan: number;
  /** Content rendered inside the expanded area */
  children: React.ReactNode;
}

/**
 * Consistent expanded-content row for desktop data tables.
 */
export const ExpandableRow: React.FC<ExpandableRowProps> = ({ isExpanded, colSpan, children }) => {
  if (!isExpanded) return null;

  return (
    <tr className="animate-fadeIn">
      <td colSpan={colSpan} className="p-0">
        <div className="p-4">
          <ExpandableDetailPanel>
          {children}
          </ExpandableDetailPanel>
        </div>
      </td>
    </tr>
  );
};

/* ── ExpandableCard (mobile) ────────────────────────────────────────── */

interface ExpandableCardProps {
  /** Whether this card section is currently expanded */
  isExpanded: boolean;
  /** Content rendered inside the expanded area */
  children: React.ReactNode;
}

/**
 * Consistent expanded-content section for mobile card views.
 * Shared detail panel background with left connector and padding.
 */
export const ExpandableCard: React.FC<ExpandableCardProps> = ({ isExpanded, children }) => {
  if (!isExpanded) return null;

  return (
    <div className="mt-3 animate-fadeIn">
      <ExpandableDetailPanel>
      {children}
      </ExpandableDetailPanel>
    </div>
  );
};
